import { averageMeetsMinimum } from "./averageCatalogs";
import {
  highestQualifyingAverage,
  matchCatalogToTournamentBowlers,
  normalizeUsbcId,
} from "./catalogAverageMatch";

const enteredAverageValue = (row) => {
  if (row?.hasAverage === false) return null;
  if (row?.average == null || row.average === "") return null;
  const value = Number(row.average);
  return Number.isFinite(value) ? value : null;
};

const sourceFor = (candidate, person) =>
  candidate?.source || person?.leagueName || person?.fileName || "Catalog";

const catalogAveragesForPerson = (person, minGamesForAverage) =>
  (person.averages || []).map((row, index) => ({
    id: row.id || `${person.id || "avg"}-${index}`,
    average: row.average,
    games: row.games ?? null,
    pins: row.pins ?? null,
    source: sourceFor(row, person),
    sourcePage: row.sourcePage ?? person.sourcePage ?? null,
    boundingBox: row.boundingBox ?? person.boundingBox ?? null,
    qualifies: averageMeetsMinimum(row.games, minGamesForAverage),
    storagePath: row.storagePath || person.storagePath || null,
    mimeType: row.mimeType || person.mimeType || null,
    fileName: row.fileName || person.fileName || null,
  }));

export const catalogRowForBowler = ({
  bowler,
  match,
  minGamesForAverage,
}) => {
  if (!match?.catalogPerson) return null;
  const person = match.catalogPerson;
  const allAverages = catalogAveragesForPerson(person, minGamesForAverage);
  const multipleAverages = allAverages.length > 1;
  const candidate = highestQualifyingAverage(
    person.averages,
    minGamesForAverage
  );
  const entered = enteredAverageValue(bowler);
  const base = {
    extractedName: person.name,
    usbcId: person.usbcId,
    average: candidate?.average ?? null,
    games: candidate?.games ?? null,
    pins: candidate?.pins ?? null,
    source: sourceFor(candidate, person),
    storagePath: candidate?.storagePath || person.storagePath,
    mimeType: candidate?.mimeType || person.mimeType,
    fileName: candidate?.fileName || person.fileName,
    sourcePage: candidate?.sourcePage ?? person.sourcePage,
    boundingBox: candidate?.boundingBox ?? person.boundingBox,
    allAverages,
    multipleAverages,
  };

  if (match.status === "possibleMatch") {
    return { ...base, kind: "possibleMatch" };
  }

  const incomingUsbc = normalizeUsbcId(person.usbcId);
  const existingUsbc = normalizeUsbcId(bowler.usbcID);
  if (incomingUsbc && existingUsbc && incomingUsbc !== existingUsbc) {
    return { ...base, kind: "usbcConflict" };
  }

  if (!candidate) {
    return {
      ...base,
      kind: "noQualifyingAverage",
      reason: `No extracted average has ${minGamesForAverage} games.`,
    };
  }

  const enteredMatchesCatalog = allAverages.some(
    (item) => entered != null && Number(item.average) === entered
  );

  if (
    entered != null &&
    entered !== Number(candidate.average) &&
    !enteredMatchesCatalog
  ) {
    return { ...base, kind: "disagrees" };
  }

  return { ...base, kind: "matched" };
};

export const buildAverageReviewRow = (bowler) => {
  const catalog = bowler.catalog;
  const entered =
    bowler.hasAverage === false ? null : enteredAverageValue(bowler);
  const noQualifying = catalog?.kind === "noQualifyingAverage";
  const hasAverage =
    bowler.hasAverage === false
      ? false
      : entered != null
      ? true
      : !noQualifying;
  return {
    id: bowler.bowlerId,
    bowlerId: bowler.bowlerId,
    name: bowler.bowlerName,
    extractedName: catalog?.extractedName || bowler.bowlerName,
    average: hasAverage ? entered ?? catalog?.average ?? null : null,
    hasAverage,
    catalogAverage: catalog?.average ?? null,
    games: catalog?.games ?? null,
    pins: catalog?.pins ?? null,
    source: catalog?.source || null,
    storagePath: catalog?.storagePath || null,
    mimeType: catalog?.mimeType || null,
    fileName: catalog?.fileName || null,
    sourcePage: catalog?.sourcePage ?? null,
    boundingBox: catalog?.boundingBox || null,
    allAverages: catalog?.allAverages || [],
    signupProofs: bowler.signupProofs || [],
    multipleAverages: Boolean(catalog?.multipleAverages),
    catalogKind: catalog?.kind || null,
    reason: catalog?.reason || null,
  };
};

export const buildCatalogMatchesByBowler = ({
  tournamentBowlers,
  catalogPeople,
  minGamesForAverage,
  averagesByBowlerId,
}) => {
  const matches = matchCatalogToTournamentBowlers(
    tournamentBowlers,
    catalogPeople
  );
  const byBowler = new Map();

  for (const match of matches) {
    const targets =
      match.status === "possibleMatch"
        ? match.candidates || []
        : tournamentBowlers.filter((bowler) => bowler.id === match.bowlerID);
    for (const bowler of targets) {
      byBowler.set(
        bowler.id,
        catalogRowForBowler({
          bowler: {
            ...bowler,
            ...(averagesByBowlerId.get(bowler.id) || {}),
          },
          match: { ...match, bowlerID: bowler.id },
          minGamesForAverage,
        })
      );
    }
  }

  return byBowler;
};
